/**
 * Typed API client, derived entirely from the GENERATED OpenAPI types.
 *
 * `openapi.d.ts` is generated by `npm run api:generate` from
 * `apps/backend/docs/openapi.json`, which the backend generates from its own
 * routers and Zod schemas. Nothing in this file names an endpoint, a request
 * shape or a response shape — every one of those is looked up from the
 * generated `paths` type, so a backend contract change becomes a TypeScript
 * error here rather than a runtime surprise in a screen.
 *
 * WHY THIS WRAPS `httpClient` INSTEAD OF FETCHING DIRECTLY
 *
 * `httpClient` already owns authentication, silent token refresh with
 * concurrent-401 deduplication, the request tracker, the `{success, data}`
 * envelope, `ApiError` normalisation, and environment routing between
 * production / training / recovery. A second transport would either duplicate
 * all of that or quietly lack it. So this layer contributes exactly one thing —
 * types — and delegates transport.
 *
 * PATH PREFIXES. Spec paths are absolute (`/api/v1/op-billing/…`) because that
 * is what the server serves. `httpClient`'s baseURL is already `/api/v1`, and it
 * differs per environment (`/training-api/v1`, `/recovered-api/v1`). So the
 * spec's `/api/v1` prefix is stripped here and the client re-adds whichever
 * prefix the selected environment needs — meaning a generated call works
 * unchanged against training and recovery, which a hardcoded absolute path
 * would not.
 */
import { httpClient, unwrap } from '../http-client';
import type { paths } from './openapi';

/** The envelope every endpoint returns. Unwrapped before the caller sees it. */
type Envelope<T> = { success: true; data: T };

type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';

/** Paths in the generated spec that implement a given method. */
type PathsWithMethod<M extends HttpMethod> = {
  [P in keyof paths]: paths[P] extends { [K in M]: unknown } ? (paths[P][M] extends never | undefined ? never : P) : never;
}[keyof paths];

type Operation<P extends keyof paths, M extends HttpMethod> = paths[P] extends { [K in M]: infer O } ? O : never;

/** The 200 response body, already unwrapped from its envelope. */
type ResponseOf<P extends keyof paths, M extends HttpMethod> =
  Operation<P, M> extends { responses: { 200: { content: { 'application/json': Envelope<infer D> } } } } ? D : unknown;

type QueryOf<P extends keyof paths, M extends HttpMethod> =
  Operation<P, M> extends { parameters: { query?: infer Q } } ? Q : never;

/**
 * The JSON request body.
 *
 * `requestBody` is emitted as OPTIONAL by the generator (`requestBody?:`), so a
 * conditional matching a REQUIRED property resolves to `never` and every body
 * argument then types as `undefined`. Reading through
 * `NonNullable<Operation[...]['requestBody']>` is what makes an optional body
 * extract correctly — this cost one real debugging cycle.
 */
type BodyOf<P extends keyof paths, M extends HttpMethod> =
  Operation<P, M> extends { requestBody?: infer R }
    ? NonNullable<R> extends { content: { 'application/json': infer B } }
      ? B
      : never
    : never;

/**
 * Spec paths are absolute; `httpClient` is mounted at the environment's API
 * base. Strip the one so the other can supply the correct prefix.
 */
const SPEC_PREFIX = '/api/v1';

function toClientPath(specPath: string): string {
  return specPath.startsWith(SPEC_PREFIX) ? specPath.slice(SPEC_PREFIX.length) : specPath;
}

/**
 * Substitute `{param}` placeholders. Values are encoded — a path parameter is
 * data, and an unencoded one is both a bug and an injection surface.
 */
function withPathParams(path: string, params?: Record<string, string | number>): string {
  if (!params) return path;
  return path.replace(/\{([^}]+)\}/g, (_match, name: string) => {
    const value = params[name];
    if (value === undefined) {
      throw new Error(`Missing path parameter "${name}" for ${path}`);
    }
    return encodeURIComponent(String(value));
  });
}

export interface RequestOptions {
  /** Values for `{param}` placeholders in the path. */
  pathParams?: Record<string, string | number>;
  /** Forwarded to axios, so a caller can pass an AbortSignal for a search box. */
  signal?: AbortSignal;
  /**
   * Extra request headers. Today only `apiPost` forwards them, for the one header the
   * server reads that is not part of the body: `Idempotency-Key`.
   */
  headers?: Record<string, string>;
}

export async function apiGet<P extends PathsWithMethod<'get'>>(
  path: P,
  query?: QueryOf<P, 'get'>,
  options?: RequestOptions,
): Promise<ResponseOf<P, 'get'>> {
  return unwrap(
    httpClient.get<Envelope<ResponseOf<P, 'get'>>>(withPathParams(toClientPath(path as string), options?.pathParams), {
      params: query ?? undefined,
      signal: options?.signal,
    }),
  );
}

export async function apiPost<P extends PathsWithMethod<'post'>>(
  path: P,
  body?: BodyOf<P, 'post'>,
  options?: RequestOptions,
): Promise<ResponseOf<P, 'post'>> {
  return unwrap(
    httpClient.post<Envelope<ResponseOf<P, 'post'>>>(
      withPathParams(toClientPath(path as string), options?.pathParams),
      body,
      { signal: options?.signal, ...(options?.headers ? { headers: options.headers } : {}) },
    ),
  );
}

/**
 * POST, keeping what `apiPost` throws away: the envelope's `meta` and the HTTP status.
 *
 * `apiPost` unwraps to `data` alone, which is right for nearly every call and
 * wrong for the one family where the server answers "done" and "already done"
 * with the same body — `POST /admissions/{id}/charges` answers 201 for a new
 * ledger row and 200 + `meta.deduplicated` when the idempotency key already
 * held one. A screen that cannot tell them apart reports a charge that was
 * never added (BCV14, 2026-10-06).
 *
 * The path and the body are still checked against the generated `paths`, which
 * is the whole reason this lives here and not as a hand-built `httpClient` call
 * in a feature service.
 */
export async function apiPostWithOutcome<P extends PathsWithMethod<'post'>>(
  path: P,
  body?: BodyOf<P, 'post'>,
  options?: RequestOptions,
): Promise<{ data: ResponseOf<P, 'post'>; meta: Record<string, unknown> | undefined; status: number }> {
  const response = await httpClient.post<Envelope<ResponseOf<P, 'post'>> & { meta?: Record<string, unknown> }>(
    withPathParams(toClientPath(path as string), options?.pathParams),
    body,
    { signal: options?.signal },
  );
  return { data: response.data.data, meta: response.data.meta, status: response.status };
}

/**
 * PATCH. Added for the ADR-081 Finance Masters, whose update route is PATCH.
 *
 * Without it a caller reaches for `apiPut` and silently issues the wrong verb —
 * Express matches no route and the request 404s, which reads like a missing
 * endpoint rather than a wrong method.
 */
export async function apiPatch<P extends PathsWithMethod<'patch'>>(
  path: P,
  body?: BodyOf<P, 'patch'>,
  options?: RequestOptions,
): Promise<ResponseOf<P, 'patch'>> {
  return unwrap(
    httpClient.patch<Envelope<ResponseOf<P, 'patch'>>>(
      withPathParams(toClientPath(path as string), options?.pathParams),
      body,
      { signal: options?.signal },
    ),
  );
}

/**
 * DELETE. Added for the IP charge drafts (2026-08-26), whose discard route is a
 * real DELETE — a draft is not a financial fact and has no audit trail to keep,
 * so it is removed rather than flagged.
 *
 * Same reasoning as `apiPatch` above: without it a caller reaches for the wrong
 * verb, Express matches no route, and a 404 reads as a missing endpoint rather
 * than as a wrong method.
 */
export async function apiDelete<P extends PathsWithMethod<'delete'>>(
  path: P,
  options?: RequestOptions,
): Promise<ResponseOf<P, 'delete'>> {
  return unwrap(
    httpClient.delete<Envelope<ResponseOf<P, 'delete'>>>(
      withPathParams(toClientPath(path as string), options?.pathParams),
      { signal: options?.signal },
    ),
  );
}

export async function apiPut<P extends PathsWithMethod<'put'>>(
  path: P,
  body?: BodyOf<P, 'put'>,
  options?: RequestOptions,
): Promise<ResponseOf<P, 'put'>> {
  return unwrap(
    httpClient.put<Envelope<ResponseOf<P, 'put'>>>(
      withPathParams(toClientPath(path as string), options?.pathParams),
      body,
      { signal: options?.signal },
    ),
  );
}

/**
 * Download a FILE endpoint — a report export, not a JSON payload.
 *
 * WHY THIS LIVES HERE AND NOT IN THE FEATURE THAT NEEDED IT (ADR-084).
 *
 * `apiGet` unwraps `{success, data}`, so it cannot serve an endpoint that
 * returns a spreadsheet or a PDF. The OP Billing Register's export needed raw
 * bytes, and the obvious shortcut — a bare `fetch()` in the feature's service
 * file — was caught by the Sprint 1 A1 fence, correctly: a raw `fetch` skips
 * the auth header, the tenant header, the environment-aware `baseURL` and the
 * 401 refresh flow that `httpClient` interceptors provide. It would have worked
 * in development and failed the moment a user switched environment.
 *
 * So the capability belongs in the transport layer, where every other request
 * already gets those things. Going through `httpClient` is the entire point.
 *
 * The filename comes from the SERVER's `Content-Disposition`. The server chose
 * the naming convention (it also stamps the CSV preamble and the PDF header);
 * a second convention invented in the browser would drift from it.
 */
export async function apiDownload(
  path: string,
  query: Record<string, unknown>,
  fallbackFilename: string,
): Promise<void> {
  const response = await httpClient.get<Blob>(toClientPath(path), {
    params: query,
    responseType: 'blob',
  });

  const disposition = String(response.headers?.['content-disposition'] ?? '');
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackFilename;

  const url = URL.createObjectURL(response.data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoked immediately: the browser already has the download, and an
  // un-revoked object URL pins the whole file in memory for the life of the
  // tab — which for a 100,000-row export is not a rounding error.
  URL.revokeObjectURL(url);
}

export type { paths };
